import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { TextAreaField, TextField } from '@/components/ui/Field';
import { FormMessage } from '@/components/ui/FormMessage';
import { Icon } from '@/components/ui/Icon';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useInvalidFieldFocus } from '@/hooks/useInvalidFieldFocus';
import { CONTACT, MAILTO_URL, WHATSAPP_URL } from '@/data/site';
import { sendContactMessage } from '@/lib/contact';
import { isNonEmpty, isValidEmail } from '@/lib/validation';
import type { FormFeedback } from '@/types';
import styles from './Contact.module.css';

interface Errors {
  name?: string;
  email?: string;
  message?: string;
}

export interface ContactProps {
  readonly titleId: string;
}

export function Contact({ titleId }: ContactProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [feedback, setFeedback] = useState<FormFeedback | null>(null);
  const [sending, setSending] = useState(false);
  const { formRef, focusFirstInvalid } = useInvalidFieldFocus();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;

    const nextErrors: Errors = {};
    if (!isNonEmpty(name)) nextErrors.name = 'الرجاء إدخال اسمك.';
    if (!isNonEmpty(email)) nextErrors.email = 'الرجاء إدخال بريدك الإلكتروني.';
    else if (!isValidEmail(email)) nextErrors.email = 'الرجاء إدخال بريد إلكتروني صحيح.';
    if (!isNonEmpty(message)) nextErrors.message = 'الرجاء كتابة رسالتك.';

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setFeedback({ tone: 'error', message: 'الرجاء تصحيح الحقول المميزة ثم إعادة المحاولة.' });
      focusFirstInvalid();
      return;
    }

    setSending(true);
    setFeedback(null);

    const result = await sendContactMessage({
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
    });

    setSending(false);

    // Only clear the form once the message is actually on its way. Wiping it on
    // a failure would destroy what the visitor wrote.
    if (!result.ok) {
      setFeedback({ tone: 'error', message: result.error ?? 'تعذّر إرسال الرسالة.' });
      return;
    }

    setName('');
    setEmail('');
    setMessage('');
    setFeedback({ tone: 'success', message: 'تم إرسال رسالتك بنجاح! سنرد عليك قريباً.' });
  };

  return (
    <>
      <SectionHeader eyebrow="تواصل معنا" title="نحن هنا لمساعدتك" titleId={titleId} />

      <div className={styles.layout}>
        <div className={styles.details}>
          <a className={styles.row} href={MAILTO_URL}>
            <Icon name="mail" size={22} className={styles.icon} />
            <span className={styles.text}>
              <span className={styles.label}>البريد الإلكتروني</span>
              <span className={`${styles.value} ${styles.ltr}`}>{CONTACT.email}</span>
            </span>
          </a>

          <a className={styles.row} href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            <Icon name="phone" size={22} className={styles.icon} />
            <span className={styles.text}>
              <span className={styles.label}>واتساب</span>
              <span className={styles.value}>
                {CONTACT.phone} — تواصل عبر واتساب
                <span className="visually-hidden"> (يفتح في نافذة جديدة)</span>
              </span>
            </span>
          </a>

          <div className={styles.row}>
            <Icon name="place" size={22} className={styles.icon} />
            <span className={styles.text}>
              <span className={styles.label}>الموقع</span>
              <span className={styles.value}>{CONTACT.location}</span>
            </span>
          </div>
        </div>

        <form ref={formRef} className={styles.form} onSubmit={submit} noValidate>
          <TextField
            label="الاسم"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="أدخل اسمك"
            autoComplete="name"
            {...(errors.name ? { error: errors.name } : {})}
          />

          <TextField
            label="البريد الإلكتروني"
            required
            type="email"
            dir="ltr"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="أدخل بريدك الإلكتروني"
            autoComplete="email"
            {...(errors.email ? { error: errors.email } : {})}
          />

          <TextAreaField
            label="الرسالة"
            required
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="أكتب رسالتك..."
            rows={5}
            {...(errors.message ? { error: errors.message } : {})}
          />

          <Button type="submit" disabled={sending}>
            {sending ? 'جارٍ الإرسال…' : 'إرسال الرسالة'}
          </Button>

          <FormMessage feedback={feedback} />
        </form>
      </div>
    </>
  );
}
